import { ValidationError } from '../../shared/errors/BaseErrors';

export const MAX_TELEGRAM_MESSAGE_LENGTH = 4096;

export type TelegramPeerType = 'user' | 'chat' | 'channel';
export type TelegramDialogKind = 'user' | 'bot' | 'group' | 'channel';
export type TelegramMessageButtonKind = 'callback' | 'url' | 'text' | 'unsupported';

export interface TelegramMessageButtonEntity {
  kind: TelegramMessageButtonKind;
  text: string;
  data?: string;
  url?: string;
}

export interface TelegramDialogMessageEntity {
  id: string;
  out: boolean;
  text: string;
  mediaType?: string;
  sentAt: string;
  senderName?: string;
  buttons?: TelegramMessageButtonEntity[][];
}

export interface TelegramPeerRef {
  peerType: TelegramPeerType;
  peerId: string;
}

export interface TelegramDialogEntity extends TelegramPeerRef {
  accessHash: string;
  kind: TelegramDialogKind;
  title: string;
  username?: string;
  unreadCount: number;
  canSend: boolean;
  lastMessage?: TelegramDialogMessageEntity;
}

export interface TelegramMessagesPage {
  offsetId?: string;
  minId?: string;
}

export interface TelegramInboxSettingsEntity {
  enabled: boolean;
  chatId?: string;
}

const CHAT_ID = /^-?\d{1,20}$/;

export const normalizeTelegramMessageText = (raw: string): string => {
  const text = (raw ?? '').trim();
  if (!text) throw new ValidationError('tg-direct-message-empty');
  if (Array.from(text).length > MAX_TELEGRAM_MESSAGE_LENGTH) throw new ValidationError('tg-direct-message-too-long');
  return text;
};

export const normalizeTelegramInboxSettings = (settings: TelegramInboxSettingsEntity): TelegramInboxSettingsEntity => {
  const chatId = (settings.chatId ?? '').trim();
  if (chatId && !CHAT_ID.test(chatId)) throw new ValidationError('tg-inbox-notify-chat-id-invalid');
  if (settings.enabled && (!chatId || /^-?0+$/.test(chatId))) throw new ValidationError('tg-inbox-notify-chat-id-required');
  return { enabled: settings.enabled, chatId: chatId || undefined };
};

export const telegramMessagePreview = (message: TelegramDialogMessageEntity): string => {
  const text = message.text.trim();
  if (!text && message.mediaType) return `[${message.mediaType}]`;
  return text;
};

export const oldestTelegramMessageId = (messages: TelegramDialogMessageEntity[]): string | undefined =>
  messages.length ? messages[messages.length - 1].id : undefined;

const PEER_TYPES: TelegramPeerType[] = ['user', 'chat', 'channel'];
const PEER_ID = /^\d{1,20}$/;

export const telegramDialogKey = (peer: TelegramPeerRef): string => `${peer.peerType}:${peer.peerId}`;

export const parseTelegramDialogKey = (raw?: string): TelegramPeerRef | undefined => {
  if (!raw) return undefined;
  const separator = raw.indexOf(':');
  if (separator === -1) return PEER_ID.test(raw) ? { peerType: 'user', peerId: raw } : undefined;
  const peerType = raw.slice(0, separator) as TelegramPeerType;
  const peerId = raw.slice(separator + 1);
  if (!PEER_TYPES.includes(peerType) || !PEER_ID.test(peerId)) return undefined;
  return { peerType, peerId };
};

export const MIN_TELEGRAM_SEARCH_QUERY_LENGTH = 2;

export type TelegramJoinStatus = 'joined' | 'request_pending' | 'opened';

export interface TelegramPeerCardEntity extends TelegramPeerRef {
  accessHash: string;
  kind: TelegramDialogKind;
  title: string;
  username?: string;
  joined: boolean;
}

export interface TelegramJoinResultEntity {
  status: TelegramJoinStatus;
  peer?: TelegramPeerCardEntity;
}

export interface TelegramCallbackAnswerEntity {
  message?: string;
  url?: string;
  alert: boolean;
}

export type TelegramJoinTargetEntity = { link: string } | { peer: TelegramPeerCardEntity };

export const normalizeTelegramSearchQuery = (raw: string): string => {
  const query = (raw ?? '').trim().replace(/^@/, '').trim();
  if (Array.from(query).length < MIN_TELEGRAM_SEARCH_QUERY_LENGTH) throw new ValidationError('tg-search-query-too-short');
  return query;
};

export const isTelegramSearchQueryValid = (raw: string): boolean => {
  try {
    normalizeTelegramSearchQuery(raw);
    return true;
  } catch {
    return false;
  }
};

export const normalizeTelegramJoinLink = (raw: string): string => {
  const link = (raw ?? '').trim();
  if (!link) throw new ValidationError('tg-join-target-invalid');
  return link;
};

export const dialogFromPeerCard = (card: TelegramPeerCardEntity): TelegramDialogEntity => ({
  peerType: card.peerType,
  peerId: card.peerId,
  accessHash: card.accessHash,
  kind: card.kind,
  title: card.title,
  username: card.username,
  unreadCount: 0,
  canSend: card.kind !== 'channel',
});

const TELEGRAM_HOSTS = ['t.me', 'www.t.me', 'telegram.me', 'www.telegram.me'];
const TELEGRAM_USERNAME = /^[A-Za-z][A-Za-z0-9_]{3,31}$/;
const TELEGRAM_INVITE_HASH = /^[A-Za-z0-9_-]+$/;
const TELEGRAM_PHONE_NUMBER = /^\d+$/;
const TELEGRAM_MESSAGE_ID = /^[1-9]\d{0,19}$/;

const isTelegramInviteHash = (hash: string): boolean => TELEGRAM_INVITE_HASH.test(hash) && !TELEGRAM_PHONE_NUMBER.test(hash);

const parseUrl = (raw: string): URL | undefined => {
  try {
    return new URL(raw);
  } catch {
    return undefined;
  }
};

export const isSafeExternalUrl = (raw?: string): boolean => {
  const url = raw ? parseUrl(raw) : undefined;
  return url?.protocol === 'https:' || url?.protocol === 'http:';
};

export const isTelegramJoinLink = (raw?: string): boolean => {
  const url = raw ? parseUrl(raw) : undefined;
  if (!url || (url.protocol !== 'https:' && url.protocol !== 'http:')) return false;
  if (!TELEGRAM_HOSTS.includes(url.hostname.toLowerCase())) return false;
  if (url.searchParams.has('start') || url.searchParams.has('startgroup')) return false;
  const segments = url.pathname.split('/').filter(Boolean);
  if (segments.length === 1 && segments[0].startsWith('+')) return isTelegramInviteHash(segments[0].slice(1));
  if (segments.length === 2 && segments[0] === 'joinchat') return isTelegramInviteHash(segments[1]);
  return segments.length === 1 && TELEGRAM_USERNAME.test(segments[0]);
};

const compareTelegramMessageIdsDesc = (a: TelegramDialogMessageEntity, b: TelegramDialogMessageEntity): number => {
  const left = BigInt(a.id);
  const right = BigInt(b.id);
  if (left === right) return 0;
  return left > right ? -1 : 1;
};

export const mergeTelegramMessages = (
  current: TelegramDialogMessageEntity[],
  latest: TelegramDialogMessageEntity[],
): TelegramDialogMessageEntity[] => {
  const byId = new Map(current.map((m) => [m.id, m]));
  latest.forEach((m) => byId.set(m.id, m));
  return Array.from(byId.values()).sort(compareTelegramMessageIdsDesc);
};

export const assertTelegramMessageId = (messageId: string): string => {
  if (!TELEGRAM_MESSAGE_ID.test(messageId ?? '')) throw new ValidationError('tg-message-id-invalid');
  return messageId;
};

export const telegramCallbackData = (button: TelegramMessageButtonEntity): string => {
  if (button.kind !== 'callback' || !button.data) throw new ValidationError('tg-callback-data-invalid');
  return button.data;
};

export const assertTelegramJoinableCard = (card: TelegramPeerCardEntity): TelegramPeerCardEntity => {
  if (card.peerType !== 'channel') throw new ValidationError('tg-join-target-invalid');
  return card;
};

export const normalizeTelegramJoinTarget = (target: TelegramJoinTargetEntity): TelegramJoinTargetEntity =>
  'link' in target ? { link: normalizeTelegramJoinLink(target.link) } : { peer: assertTelegramJoinableCard(target.peer) };

export const newestTelegramMessageId = (messages: TelegramDialogMessageEntity[]): string | undefined =>
  messages.length ? messages[0].id : undefined;
